import {
  Car,
  IncentivesProgramOffer,
} from '@upstart/harmony/lib/legos/carTypes';
import {
  LeaseOffer,
  PurchaseOffer,
  RawLeaseOffer,
  RawPurchaseOffer,
} from '@upstart/harmony/lib/legos/paymentTypes';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes';
import { useMemo, useEffect, useState } from 'react';
import { get, uniqBy } from 'lodash';
import {
  CLIENT_TYPE,
  Condition,
  FinanceType,
  LenderNames,
  LENDER_KEY_TO_LABEL,
  Origin,
} from '@upstart/harmony/lib/constants';
import { CreditPricing } from '@upstart/harmony/lib/legos/creditPricingType';
import DealerPricingService from 'services/DealerPricingService';
import {
  addMarkupToLeaseRates,
  addMarkupToPurchaseApr,
  filterIncentives,
} from '../offer-utils';

const filterIncentiveOffers = (
  activeOffer: LeaseOffer,
  incentiveOffers: IncentivesProgramOffer[],
) => {
  // Filter out mismatched months
  const monthFiltered = filterIncentives(incentiveOffers, activeOffer);

  // Filter out duplicates
  // The filter keeps the first duplicate so lowest APR (prime) is kept.
  return uniqBy(monthFiltered, offer => get(offer, 'program.description'));
};

export function useIncentives({
  showHiddenIncentives,
  car,
  financeType,
  activeOffer,
  dealer,
  deal,
  isOnePay,
}: {
  showHiddenIncentives: boolean;
  car: Car;
  financeType: FinanceType;
  activeOffer: LeaseOffer;
  deal: Deal;
  dealer: Dealer;
  isOnePay: boolean;
}) {
  const [
    creditPricingConfig,
    setCreditPricingConfig,
  ] = useState<CreditPricing | null>(null);

  // load config from backend
  const loadPricingRules = async () => {
    const dealerPricingConfig = await DealerPricingService.get({
      dealerId: dealer.id,
      section: {
        type:
          deal.origin === Origin.ONLINE
            ? CLIENT_TYPE.ONLINE
            : CLIENT_TYPE.IN_STORE,
        condition: car.condition ?? Condition.NEW,
        year: car.year ? car.year.toString() : undefined,
        make: car.make ?? undefined,
        model: car.model ?? undefined,
        trim: car.trim ?? undefined,
        vin: car.id,
      },
    });
    setCreditPricingConfig(dealerPricingConfig);
  };

  useEffect(() => {
    loadPricingRules().catch(_err => setCreditPricingConfig(null));
  }, []);

  return useMemo(() => {
    if (!activeOffer) {
      return [];
    }
    let newIncentiveOffers = (get(car, 'incentiveOffers') || []).filter(o =>
      o.financeTypes.includes(financeType),
    );

    newIncentiveOffers = removeUnselectedThirdPartyIncentives(
      creditPricingConfig,
      newIncentiveOffers,
    );

    if (
      financeType === FinanceType.LEASE ||
      financeType === FinanceType.PURCHASE
    ) {
      // Hide ineligible incentives, if applicable.
      if (!showHiddenIncentives) {
        newIncentiveOffers = filterIncentiveOffers(
          activeOffer,
          newIncentiveOffers,
        );
      }
      if (financeType === FinanceType.LEASE) {
        newIncentiveOffers = addMarkupToLeaseRates(
          newIncentiveOffers as RawLeaseOffer[],
          activeOffer,
          deal,
          dealer,
          isOnePay,
        ) as IncentivesProgramOffer[];
      } else if (financeType === FinanceType.PURCHASE) {
        newIncentiveOffers = addMarkupToPurchaseApr(
          newIncentiveOffers as RawPurchaseOffer[],
          (activeOffer as any) as PurchaseOffer,
          deal,
          dealer,
        ) as IncentivesProgramOffer[];
      }
    }
    return newIncentiveOffers;
  }, [
    activeOffer,
    car,
    financeType,
    showHiddenIncentives,
    creditPricingConfig,
  ]);
}

const removeUnselectedThirdPartyIncentives = (
  creditPricingConfig: CreditPricing | null,
  incentiveOffers: IncentivesProgramOffer[],
) => {
  const enableThirdPartyLenders =
    creditPricingConfig?.options?.enableThirdPartyLenders;

  if (enableThirdPartyLenders) {
    const thirdPartyIncentives =
      creditPricingConfig?.options?.thirdPartyLenders || {};

    const selectedThirdPartyIncentives = (Object.keys(
      thirdPartyIncentives,
    ) as LenderNames[])
      .filter((lender: LenderNames) => thirdPartyIncentives[lender])
      .map(lender => LENDER_KEY_TO_LABEL[lender].trim().toLowerCase());

    const selectedThirdPartyIncentivesSet = new Set(
      selectedThirdPartyIncentives,
    );
    return incentiveOffers.filter(offer => {
      if (
        offer.program.isThirdPartyIncentive &&
        selectedThirdPartyIncentivesSet.has(
          offer.program.source.trim().toLowerCase(),
        )
      ) {
        return true;
      }

      if (!offer.program.isThirdPartyIncentive) {
        return true;
      }
      return false;
    });
  }

  // explicitly checking for false value
  if (enableThirdPartyLenders === false) {
    return incentiveOffers.filter(
      offer => !offer.program.isThirdPartyIncentive,
    );
  }

  return incentiveOffers;
};
